import { useEffect, useRef } from "react";
import { Badge, Box, Button, IconButton, Popper, ClickAwayListener } from "@mui/material";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useActiveUploads, usePackagesActions, usePackagesState, UploadClientStatus, isActiveUpload } from "@/store";
import { UploadProgressIcon, CheckCircleSmIcon, XCircleIcon, UploadingIcon, ChevronIcon } from "@/icons";
import {
    UploadProgressPanel,
    UploadProgressCard,
    UploadProgressCardContent,
    UploadProgressIconWrapper,
    UploadProgressInfo,
    UploadProgressTitle,
    UploadProgressMessage,
    UploadProgressCardRow,
    UploadProgressCardActions,
    UploadProgressButton,
    UploadProgressSecondButton,
    UploadAbsCrossButton,
} from "./UploadProgress.elements";
import { UploadProgressLoadingCard } from "./UploadProgressLoadingCard";

// Popup shows at most ONE active package card — anything more goes on the /uploads/in-progress
// page. Keeps the popup small enough to glance at; the page is the real management surface for
// many parallel uploads. Terminal cards (completed/failed/canceled) still render in full since
// the user typically wants to retry/dismiss them right away.
const ACTIVE_POPUP_CAP = 1;

export const UploadProgress = () => {
    const anchorElRef = useRef(null);
    const location = useLocation();
    const { isProgressDropdownOpen, uploadingPackages, isRetryUploading } = usePackagesState();
    const { setIsProgressDropdownOpen, retryUploadPackage, deleteUploadingPackage } = usePackagesActions();
    // useActiveUploads orders by createdAt asc (FIFO), so the head is the oldest in-flight
    // package — which is also the active uploader since the queue runs oldest-first.
    // Called unconditionally before any early return — rules-of-hooks.
    const activeUploads = useActiveUploads();

    // The /uploads/in-progress page is the same surface, so the header popup would be
    // redundant noise (and a confusing second close target). Hide the trigger entirely and
    // make sure the dropdown closes if the user navigates here while it's open.
    const isOnInProgressPage = location.pathname === "/uploads/in-progress";

    useEffect(() => {
        if (isOnInProgressPage && isProgressDropdownOpen) {
            setIsProgressDropdownOpen(false);
        }
    }, [isOnInProgressPage, isProgressDropdownOpen, setIsProgressDropdownOpen]);

    if (isOnInProgressPage) {
        return null;
    }

    const closeDropdown = () => setIsProgressDropdownOpen(false);
    const toggleDropdown = () => setIsProgressDropdownOpen(!isProgressDropdownOpen);

    const terminalUploads = uploadingPackages.filter((u) => !isActiveUpload(u));
    const visibleActive = activeUploads.slice(0, ACTIVE_POPUP_CAP);
    const overflowActive = activeUploads.length - visibleActive.length;

    return (
        <>
            <IconButton ref={anchorElRef} onClick={toggleDropdown}>
                <Badge badgeContent={uploadingPackages.length} color="secondary">
                    <UploadProgressIcon />
                </Badge>
            </IconButton>

            <Popper
                anchorEl={anchorElRef.current}
                open={isProgressDropdownOpen}
                placement="bottom"
                modifiers={[
                    { name: "offset", options: { offset: [0, 10] } },
                    { name: "preventOverflow", options: { padding: 12 } },
                ]}
                sx={{
                    zIndex: 12,
                    width: "auto",
                    maxWidth: "calc(100vw - 24px)",
                }}
            >
                <ClickAwayListener
                    onClickAway={(e) => {
                        if (anchorElRef.current && anchorElRef.current.contains(e.target)) return;
                        closeDropdown();
                    }}
                >
                    <div>
                        <UploadProgressPanel>
                            {uploadingPackages.length === 0 && (
                                <Box
                                    sx={{
                                        color: "#F7FAFAA6",
                                        fontSize: "14px",
                                        padding: "8px 6px",
                                        textAlign: "center",
                                        width: "100%",
                                    }}
                                >
                                    There are no active uploads right now
                                </Box>
                            )}

                            {visibleActive.map((upload) => (
                                <UploadProgressCard key={upload.uploadId}>
                                    <UploadProgressCardContent>
                                        <UploadProgressLoadingCard upload={upload} compact />
                                    </UploadProgressCardContent>
                                </UploadProgressCard>
                            ))}

                            {terminalUploads.map((upload) => (
                                <UploadProgressCard key={upload.uploadId}>
                                    <UploadProgressCardContent>
                                        {upload.status === UploadClientStatus.COMPLETED && (
                                            <UploadProgressCardRow>
                                                <UploadProgressIconWrapper as={CheckCircleSmIcon} status={upload.status} />
                                                <UploadProgressInfo>
                                                    <UploadProgressTitle>{upload.uploadMetadata.packageName}</UploadProgressTitle>
                                                    <UploadProgressMessage>Your file was successfully uploaded.</UploadProgressMessage>
                                                    {upload.statusMessage && (
                                                      <UploadProgressMessage>{upload.statusMessage}</UploadProgressMessage>
                                                    )}
                                                </UploadProgressInfo>
                                                <UploadAbsCrossButton onClick={() => deleteUploadingPackage(upload.uploadId)} />
                                            </UploadProgressCardRow>
                                        )}

                                        {upload.status === UploadClientStatus.VERIFYING && (
                                            <UploadProgressCardRow>
                                                <UploadProgressIconWrapper as={UploadingIcon} status={upload.status} />
                                                <UploadProgressInfo>
                                                    <UploadProgressTitle>{upload.uploadMetadata.packageName}</UploadProgressTitle>
                                                    <UploadProgressMessage>
                                                        {upload.statusMessage ?? "Connection interrupted. Checking whether the upload was received..."}
                                                    </UploadProgressMessage>
                                                    <UploadProgressCardActions>
                                                        <UploadProgressButton
                                                            onClick={() => retryUploadPackage(upload.uploadId)}
                                                            disabled={isRetryUploading}
                                                        >
                                                            Retry
                                                        </UploadProgressButton>
                                                        <UploadProgressSecondButton onClick={() => deleteUploadingPackage(upload.uploadId)}>
                                                            Cancel
                                                        </UploadProgressSecondButton>
                                                    </UploadProgressCardActions>
                                                </UploadProgressInfo>
                                                <UploadAbsCrossButton onClick={() => deleteUploadingPackage(upload.uploadId)} />
                                            </UploadProgressCardRow>
                                        )}

                                        {upload.status === UploadClientStatus.FAILED && (
                                            <UploadProgressCardRow>
                                                <UploadProgressIconWrapper as={XCircleIcon} status={upload.status} />
                                                <UploadProgressInfo>
                                                    <UploadProgressTitle>{upload.uploadMetadata.packageName}</UploadProgressTitle>
                                                    <UploadProgressMessage>
                                                        {upload.statusMessage ?? "Your file could not be uploaded. Would you like to try again?"}
                                                    </UploadProgressMessage>
                                                    <UploadProgressCardActions>
                                                        <UploadProgressButton
                                                            onClick={() => retryUploadPackage(upload.uploadId)}
                                                            disabled={isRetryUploading}
                                                        >
                                                            Retry
                                                        </UploadProgressButton>
                                                        <UploadProgressSecondButton onClick={() => deleteUploadingPackage(upload.uploadId)}>
                                                            Cancel
                                                        </UploadProgressSecondButton>
                                                    </UploadProgressCardActions>
                                                </UploadProgressInfo>
                                                <UploadAbsCrossButton onClick={() => deleteUploadingPackage(upload.uploadId)} />
                                            </UploadProgressCardRow>
                                        )}

                                        {upload.status === UploadClientStatus.CANCELED && (
                                            <UploadProgressCardRow>
                                                <UploadProgressIconWrapper as={XCircleIcon} status={upload.status} />
                                                <UploadProgressInfo>
                                                    <UploadProgressTitle>{upload.uploadMetadata.packageName}</UploadProgressTitle>
                                                    <UploadProgressMessage>Your upload has been canceled.</UploadProgressMessage>
                                                </UploadProgressInfo>
                                                <UploadAbsCrossButton onClick={() => deleteUploadingPackage(upload.uploadId)} />
                                            </UploadProgressCardRow>
                                        )}
                                    </UploadProgressCardContent>
                                </UploadProgressCard>
                            ))}

                            {activeUploads.length > 1 && (
                                <Button
                                    component={RouterLink}
                                    to="/uploads/in-progress"
                                    onClick={closeDropdown}
                                    fullWidth
                                    variant="contained"
                                    endIcon={
                                        <Box
                                            component={ChevronIcon}
                                            sx={{
                                                transform: "rotate(-90deg)",
                                                width: 9,
                                                height: 9,
                                                mr: 0.3,
                                                color: "rgba(247,250,250,0.82)",
                                            }}
                                        />
                                    }
                                    sx={{
                                        textTransform: "none",
                                        fontWeight: 700,
                                        fontSize: "13px",
                                        py: 1.25,
                                        px: 1.4,
                                        minHeight: 44,
                                        borderRadius: "10px",
                                        backgroundColor: "#6366C7",
                                        color: "rgba(247,250,250,0.95)",
                                        border: "1px solid rgba(139,148,255,0.35)",
                                        boxShadow: "none",
                                        justifyContent: "center",
                                        "& .MuiButton-endIcon": {
                                            ml: 0.8,
                                            mr: 0,
                                        },
                                        "&:hover": {
                                            backgroundColor: "#7174D8",
                                            borderColor: "rgba(139,148,255,0.55)",
                                            boxShadow: "none",
                                        },
                                    }}
                                >
                                    View all uploadings
                                    {overflowActive > 0 && (
                                        <Box
                                            component="span"
                                            sx={{
                                                ml: 1,
                                                px: 0.75,
                                                py: 0.05,
                                                borderRadius: "999px",
                                                fontSize: "10px",
                                                fontWeight: 800,
                                                lineHeight: 1.5,
                                                backgroundColor: "rgba(11,16,28,0.35)",
                                                color: "rgba(247,250,250,0.95)",
                                                border: "1px solid rgba(255,255,255,0.18)",
                                            }}
                                        >
                                            +{overflowActive}
                                        </Box>
                                    )}
                                </Button>
                            )}
                        </UploadProgressPanel>
                    </div>
                </ClickAwayListener>
            </Popper>
        </>
    );
};
